import i18n from '@/utils/i18n';
import React, { useMemo, useState } from 'react';
import { ActionSheet } from '@/components/action-sheet';
import { BlurView } from 'expo-blur';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { SCREEN_PADDING } from '@/styles';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import {
  useOfflineDownloadManagementContext,
  useSelectedOfflineRestaurantUUIDs,
} from '@/contexts/offline-download-management-context';
import { View } from 'react-native';

interface Props {
  onAction: () => void;
  syncedRestaurantUUIDs: string[];
  updateDataStatus: () => Promise<void>;
}

export const OfflineManagementActions: React.FC<Props> = ({
  onAction,
  syncedRestaurantUUIDs,
  updateDataStatus,
}) => {
  const [styles] = useStyles();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const {
    selectedRestaurantRelationship: { supplierUUID },
  } = useAuthenticatedUserContext();

  const { downloadSelectedRestaurants, removeSelectedRestaurants } =
    useOfflineDownloadManagementContext((s) => ({
      downloadSelectedRestaurants: s.downloadSelectedRestaurants,
      removeSelectedRestaurants: s.removeSelectedRestaurants,
    }));
  const selectedOfflineRestaurantUUIDs = useSelectedOfflineRestaurantUUIDs();
  const selectedOfflineRestaurantCount = selectedOfflineRestaurantUUIDs.size;

  const selectedSyncedRestaurantUUIDs = useMemo(() => {
    return syncedRestaurantUUIDs.filter((uuid) =>
      selectedOfflineRestaurantUUIDs.has(uuid),
    );
  }, [selectedOfflineRestaurantUUIDs, syncedRestaurantUUIDs]);

  const selectedSyncedRestaurantCount = selectedSyncedRestaurantUUIDs.length;
  const isDeleteButtonDisabled = selectedSyncedRestaurantCount === 0;

  const trackingProperties = useMemo(() => {
    return {
      count: selectedOfflineRestaurantCount,
      selectedRestaurantUUIDs: [...selectedOfflineRestaurantUUIDs],
      selectedSyncedRestaurantUUIDs,
      supplierUUID,
    };
  }, [
    selectedOfflineRestaurantCount,
    selectedOfflineRestaurantUUIDs,
    selectedSyncedRestaurantUUIDs,
    supplierUUID,
  ]);

  return (
    <BlurView experimentalBlurMethod="dimezisBlurView" style={styles.blur}>
      <View style={styles.buttonContainer}>
        <Button
          onPress={() => {
            downloadSelectedRestaurants(updateDataStatus);
            onAction();
          }}
          trackingData={{
            name: 'location_picker.sync_selected_button',
            properties: trackingProperties,
          }}
        >
          {i18n.t(
            selectedOfflineRestaurantCount === 1
              ? 'sync_selected_account.singular'
              : 'sync_selected_account.plural',
            {
              count: selectedOfflineRestaurantCount,
            },
          )}
        </Button>
        <Button
          disabled={isDeleteButtonDisabled}
          onPress={() => setShowDeleteConfirmation(true)}
          style={styles.deleteButton}
          textStyle={
            isDeleteButtonDisabled ? styles.deleteButtonDisabledText : null
          }
          trackingData={{
            name: 'location_picker.delete_offline_data_button',
            properties: trackingProperties,
          }}
          type="danger"
        >
          {i18n.t('delete_offline_data')}
        </Button>
      </View>
      <ActionSheet
        actions={[i18n.t('cancel'), i18n.t('delete')]}
        dangerIndices={[1]}
        horizontalButtons
        isVisible={showDeleteConfirmation}
        message={
          <View style={styles.deleteConfirmation}>
            <Typography variant="paragraph-base">
              {i18n.t('this_action_can_not_be_undone')}
            </Typography>
          </View>
        }
        onAction={async (_, i) => {
          setShowDeleteConfirmation(false);

          if (i === 1) {
            await removeSelectedRestaurants();
            await updateDataStatus();
            onAction();
          }
        }}
        onClose={() => setShowDeleteConfirmation(false)}
        title={i18n.t(
          selectedSyncedRestaurantCount === 1
            ? 'delete_offline_data_confirmation.singular'
            : 'delete_offline_data_confirmation.plural',
          {
            count: selectedSyncedRestaurantCount,
          },
        )}
        titleStyle={styles.leftAlign}
      />
    </BlurView>
  );
};

const useStyles = createStyles((theme) => ({
  blur: {
    bottom: 0,
    left: 0,
    position: 'absolute',
    right: 0,
  },
  buttonContainer: {
    gap: 8,
    padding: SCREEN_PADDING,
  },
  deleteButton: {
    backgroundColor: theme.colors.transparent,
  },
  deleteButtonDisabledText: {
    color: theme.sk.color.text.disabled,
  },
  deleteConfirmation: {
    marginBottom: 24,
  },
  leftAlign: {
    textAlign: 'left',
  },
}));
